import React from 'react';
import { clsx } from 'clsx';
import {
  Plus,
  Pencil,
  Trash2,
  Archive,
  RotateCcw,
  ArrowRightLeft,
  UserCheck,
  AlertTriangle,
  LogIn,
  LogOut,
  KeyRound,
  ShieldAlert,
  Download,
  Settings,
  UserCog,
  Activity,
} from 'lucide-react';
import { Card } from '../ui/Card';
import { AuditLog, AUDIT_ACTION_LABELS, AuditAction } from '../../types/audit';

interface RecentActivityCardProps {
  entries: AuditLog[];
  loading?: boolean;
  error?: string | null;
  className?: string;
}

const ACTION_ICONS: Record<AuditAction, React.ReactNode> = {
  CREATE: <Plus className="w-3.5 h-3.5" />,
  UPDATE: <Pencil className="w-3.5 h-3.5" />,
  DELETE: <Trash2 className="w-3.5 h-3.5" />,
  ARCHIVE: <Archive className="w-3.5 h-3.5" />,
  RESTORE: <RotateCcw className="w-3.5 h-3.5" />,
  STATUS_CHANGE: <ArrowRightLeft className="w-3.5 h-3.5" />,
  ASSIGNMENT: <UserCheck className="w-3.5 h-3.5" />,
  PRIORITY_CHANGE: <AlertTriangle className="w-3.5 h-3.5" />,
  LOGIN: <LogIn className="w-3.5 h-3.5" />,
  LOGOUT: <LogOut className="w-3.5 h-3.5" />,
  PASSWORD_CHANGE: <KeyRound className="w-3.5 h-3.5" />,
  FAILED_LOGIN: <ShieldAlert className="w-3.5 h-3.5" />,
  EXPORT: <Download className="w-3.5 h-3.5" />,
  CONFIG_CHANGE: <Settings className="w-3.5 h-3.5" />,
  ROLE_CHANGE: <UserCog className="w-3.5 h-3.5" />,
};

const TONE: Record<string, string> = {
  success: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  info: 'bg-sky-500/10 border-sky-500/20 text-sky-400',
  warning: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
  danger: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
  neutral: 'bg-slate-700/50 border-slate-600/50 text-slate-300',
};

const ACTION_TONE: Record<AuditAction, keyof typeof TONE> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'danger',
  ARCHIVE: 'warning',
  RESTORE: 'success',
  STATUS_CHANGE: 'info',
  ASSIGNMENT: 'info',
  PRIORITY_CHANGE: 'warning',
  LOGIN: 'success',
  LOGOUT: 'neutral',
  PASSWORD_CHANGE: 'warning',
  FAILED_LOGIN: 'danger',
  EXPORT: 'info',
  CONFIG_CHANGE: 'info',
  ROLE_CHANGE: 'info',
};

function relativeTime(ts: string): string {
  const date = new Date(ts);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export const RecentActivityCard: React.FC<RecentActivityCardProps> = ({
  entries,
  loading = false,
  error = null,
  className,
}) => {
  return (
    <Card title="Recent Activity" subtitle="Latest system events" className={className}>
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-7 h-7 rounded-lg bg-slate-800/80" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-slate-800/80 rounded w-3/4" />
                <div className="h-2.5 bg-slate-800/60 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <p className="text-xs text-rose-400 py-4">{error}</p>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
          <Activity className="w-8 h-8 mb-2 text-slate-600" />
          <p className="text-xs">No recent activity</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800/60">
          {entries.map((entry) => (
            <div key={entry.log_id} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
              <div
                className={clsx(
                  'w-7 h-7 rounded-lg border flex items-center justify-center flex-shrink-0 mt-0.5',
                  TONE[ACTION_TONE[entry.action]]
                )}
              >
                {ACTION_ICONS[entry.action]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-200 truncate">
                  {entry.description || AUDIT_ACTION_LABELS[entry.action]}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-slate-500">
                  <span>{AUDIT_ACTION_LABELS[entry.action]}</span>
                  <span aria-hidden="true">·</span>
                  <span className="truncate">{entry.user_name || 'System'}</span>
                  <span aria-hidden="true">·</span>
                  <span className="whitespace-nowrap">{relativeTime(entry.timestamp)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
